import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/Edit';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { timeEntriesApi, tenantApi, projectsApi, leaveRequestsApi } from '../services/api';
import { WorkerLeaveSection } from '../components/LeaveRequests';
import { useSearchParams } from 'react-router-dom';
import {
  formatTimeEntryDate,
  formatTimeEntryDateTime,
  formatTimeEntryTime,
  getTimeEntryDayKey,
  parseTimeEntryDate,
} from '../utils/timeEntries';

// Get week boundaries (Monday to Sunday)
function getWeekBoundaries(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  
  const start = new Date(d.setDate(diff));
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  
  return { start, end };
}

// Format duration
function formatDuration(hours) {
  if (hours === null || hours === undefined) return '-';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
}

// Format elapsed time (seconds to HH:MM:SS)
function formatElapsedTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function formatClockOutProjectLabel(project) {
  if (!project) {
    return '';
  }

  if (project.project_id === NOT_ALLOCATED_PROJECT_SENTINEL_ID) {
    return project.organisation_name || 'Not allocated';
  }

  return project.quote_reference || project.city || 'Project';
}

function getClockOutProjectSecondaryLabel(project) {
  if (!project || project.project_id === NOT_ALLOCATED_PROJECT_SENTINEL_ID) {
    return '';
  }

  return project.organisation_name || '';
}

const NOT_ALLOCATED_PROJECT_SENTINEL_ID = -1;
const NOT_ALLOCATED_PROJECT = {
  project_id: NOT_ALLOCATED_PROJECT_SENTINEL_ID,
  organisation_name: 'Not allocated',
  quote_reference: '',
};

function getClockOutProjectOptions(projects = []) {
  return [NOT_ALLOCATED_PROJECT, ...projects];
}

function getClockOutProjectPayloadValue(projectId) {
  if (projectId === '' || projectId === null || projectId === undefined) {
    return {};
  }

  const parsedProjectId = Number(projectId);
  if (Number.isNaN(parsedProjectId)) {
    return {};
  }

  if (parsedProjectId === NOT_ALLOCATED_PROJECT_SENTINEL_ID) {
    return { project_unallocated: true };
  }

  return { project_id: parsedProjectId };
}

// Manual Entry Dialog Component
function ManualEntryDialog({
  open,
  onClose,
  onSubmit,
  isClockedIn = false,
  activeClockInTime,
  requireProjectOnClockOut = false,
  availableProjects = [],
  availableProjectsLoading = false,
}) {
  const [clockInTime, setClockInTime] = useState(new Date());
  const [clockOutTime, setClockOutTime] = useState(null);
  const [includeClockOut, setIncludeClockOut] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const projectOptions = getClockOutProjectOptions(availableProjects);
  const selectedProject = projectOptions.find(
    (project) => String(project.project_id) === String(selectedProjectId)
  ) || null;

  // Initialize clock out date when dialog opens and user is clocked in
  useEffect(() => {
    if (open && isClockedIn && !clockOutTime) {
      setClockOutTime(new Date());
    }
  }, [clockOutTime, isClockedIn, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (availableProjects.length === 1) {
      setSelectedProjectId(String(availableProjects[0].project_id));
      return;
    }
    if (availableProjects.length === 0) {
      setSelectedProjectId(String(NOT_ALLOCATED_PROJECT_SENTINEL_ID));
      return;
    }
    setSelectedProjectId('');
  }, [availableProjects, open]);

  const handleSubmit = async () => {
    if (!notes.trim()) {
      setError('Please provide a reason for this manual entry');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let data;
      const requiresProjectSelection = requireProjectOnClockOut && (isClockedIn || includeClockOut);
      const selectedProjectPayload = getClockOutProjectPayloadValue(selectedProjectId);

      if (requiresProjectSelection && !selectedProjectId) {
        setError('Please select the project you worked on');
        setLoading(false);
        return;
      }

      if (isClockedIn && activeClockInTime) {
        // User is clocked in — only submit clock out time
        if (!clockOutTime) {
          setError('Please select a clock out time');
          setLoading(false);
          return;
        }

        const activeClockIn = parseTimeEntryDate(activeClockInTime);
        if (clockOutTime <= activeClockIn) {
          setError('Clock out time must be after your clock in time');
          setLoading(false);
          return;
        }

        if (clockOutTime > new Date()) {
          setError('Clock out time cannot be in the future');
          setLoading(false);
          return;
        }

        data = {
          clock_in_time: activeClockInTime,
          clock_out_time: clockOutTime.toISOString(),
          notes: notes.trim(),
          ...(requiresProjectSelection ? selectedProjectPayload : {}),
        };
      } else {
        // User is clocked out — submit clock in (and optionally clock out)
        data = {
          clock_in_time: clockInTime.toISOString(),
          notes: notes.trim(),
        };

        if (includeClockOut && clockOutTime) {
          if (clockOutTime <= clockInTime) {
            setError('Clock out time must be after clock in time');
            setLoading(false);
            return;
          }
          data.clock_out_time = clockOutTime.toISOString();
          if (requiresProjectSelection) {
            Object.assign(data, selectedProjectPayload);
          }
        }
      }

      await onSubmit(data);
      setClockInTime(new Date());
      setClockOutTime(null);
      setIncludeClockOut(false);
      setNotes('');
      setSelectedProjectId('');
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to submit entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isClockedIn ? 'Manual Clock Out' : 'Manual Clock In'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          {/* If clocked in, show the current clock-in time as info */}
          {isClockedIn && activeClockInTime ? (
            <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Currently clocked in at {formatTimeEntryDateTime(activeClockInTime, {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </Alert>
              <DateTimePicker
                label="Clock Out Time"
                value={clockOutTime}
                onChange={(newValue) => setClockOutTime(newValue)}
                slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
              />
            </>
          ) : (
            <>
              <DateTimePicker
                label="Clock In Time"
                value={clockInTime}
                onChange={(newValue) => setClockInTime(newValue)}
                slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeClockOut}
                    onChange={(e) => {
                      setIncludeClockOut(e.target.checked);
                      if (e.target.checked && !clockOutTime) {
                        setClockOutTime(new Date());
                      }
                    }}
                  />
                }
                label="Include Clock Out Time"
                sx={{ mt: 1 }}
              />

              {includeClockOut && (
                <DateTimePicker
                  label="Clock Out Time"
                  value={clockOutTime}
                  onChange={(newValue) => setClockOutTime(newValue)}
                  slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                />
              )}
            </>
          )}

          <TextField
            label="Reason (Required)"
            multiline
            rows={3}
            fullWidth
            margin="normal"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why are you submitting a manual entry?"
          />

          {requireProjectOnClockOut && (isClockedIn || includeClockOut) && (
            <>
              <Autocomplete
                fullWidth
                options={projectOptions}
                value={selectedProject}
                onChange={(_, newValue) => setSelectedProjectId(newValue ? String(newValue.project_id) : '')}
                getOptionLabel={formatClockOutProjectLabel}
                isOptionEqualToValue={(option, value) => option.project_id === value.project_id}
                loading={availableProjectsLoading}
                disabled={availableProjectsLoading}
                noOptionsText="No matching projects found"
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatClockOutProjectLabel(option)}
                      </Typography>
                      {getClockOutProjectSecondaryLabel(option) ? (
                        <Typography variant="caption" color="text.secondary">
                          {getClockOutProjectSecondaryLabel(option)}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    label="Project Worked On"
                    helperText={availableProjectsLoading
                      ? 'Loading your assigned projects...'
                      : availableProjects.length === 0
                        ? 'No assigned projects found. Choose Not allocated to save this time entry without a project.'
                        : 'Search or select the project you worked on, or choose Not allocated.'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {availableProjectsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ClockOutProjectDialog({
  open,
  onClose,
  onConfirm,
  projects,
  selectedProjectId,
  onProjectChange,
  loading,
}) {
  const projectOptions = getClockOutProjectOptions(projects);
  const selectedProject = projectOptions.find(
    (project) => String(project.project_id) === String(selectedProjectId)
  ) || null;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Project</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Choose the project you worked on before clocking out, or select Not allocated.
        </Typography>

        <Autocomplete
          fullWidth
          options={projectOptions}
          value={selectedProject}
          onChange={(_, newValue) => onProjectChange(newValue ? String(newValue.project_id) : '')}
          getOptionLabel={formatClockOutProjectLabel}
          isOptionEqualToValue={(option, value) => option.project_id === value.project_id}
          disabled={loading}
          noOptionsText="No matching projects found"
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatClockOutProjectLabel(option)}
                </Typography>
                      {getClockOutProjectSecondaryLabel(option) ? (
                  <Typography variant="caption" color="text.secondary">
                          {getClockOutProjectSecondaryLabel(option)}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Worked On"
              helperText={projects.length === 0
                ? 'No assigned Sales Order projects found. Choose Not allocated to clock out without assigning a project.'
                : 'Search or select the project to save against today\'s time entry, or choose Not allocated.'}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" disabled={loading || !selectedProjectId}>
          {loading ? <CircularProgress size={24} /> : 'Clock Out'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getEntryStatus(entry) {
  if (entry.is_queried) {
    return { label: 'Challenged', color: 'error' };
  }

  if (entry.is_approved) {
    return { label: 'Approved', color: 'success' };
  }

  return { label: 'Pending', color: 'warning' };
}

// Edit Entry Dialog Component
function EditEntryDialog({ open, entry, onClose, onSave, availableProjects = [], availableProjectsLoading = false }) {
  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [notes, setNotes] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const projectOptions = getClockOutProjectOptions(availableProjects);
  const selectedProject = projectOptions.find(
    (project) => String(project.project_id) === String(selectedProjectId)
  ) || null;

  useEffect(() => {
    if (entry) {
      setClockInTime(parseTimeEntryDate(entry.clock_in_time));
      setClockOutTime(entry.clock_out_time ? parseTimeEntryDate(entry.clock_out_time) : null);
      setNotes('');
      setSelectedProjectId(
        entry.project_id === null || entry.project_id === undefined
          ? String(NOT_ALLOCATED_PROJECT_SENTINEL_ID)
          : String(entry.project_id)
      );
      setError(null);
    }
  }, [entry]);

  useEffect(() => {
    if (
      selectedProjectId === ''
      || Number(selectedProjectId) === NOT_ALLOCATED_PROJECT_SENTINEL_ID
      || availableProjectsLoading
    ) {
      return;
    }

    const selectedProjectStillAssigned = availableProjects.some(
      (project) => String(project.project_id) === String(selectedProjectId)
    );

    if (!selectedProjectStillAssigned) {
      setSelectedProjectId(String(NOT_ALLOCATED_PROJECT_SENTINEL_ID));
    }
  }, [availableProjects, availableProjectsLoading, selectedProjectId]);

  const handleSave = async () => {
    if (!notes.trim()) {
      setError('Please provide a reason for the change');
      return;
    }

    if (clockOutTime && clockOutTime <= clockInTime) {
      setError('Clock out time must be after clock in time');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        clock_in_time: clockInTime.toISOString(),
        notes: notes.trim(),
      };

      if (clockOutTime) {
        data.clock_out_time = clockOutTime.toISOString();
      }

      Object.assign(data, getClockOutProjectPayloadValue(selectedProjectId));

      await onSave(entry.time_entry_id, data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update entry');
    } finally {
      setLoading(false);
    }
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Time Entry</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <DateTimePicker
            label="Clock In Time"
            value={clockInTime}
            onChange={(newValue) => setClockInTime(newValue)}
            slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
          />

          {clockOutTime && (
            <DateTimePicker
              label="Clock Out Time"
              value={clockOutTime}
              onChange={(newValue) => setClockOutTime(newValue)}
              slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
            />
          )}

          <Autocomplete
            fullWidth
            options={projectOptions}
            value={selectedProject}
            onChange={(_, newValue) => setSelectedProjectId(newValue ? String(newValue.project_id) : '')}
            getOptionLabel={formatClockOutProjectLabel}
            isOptionEqualToValue={(option, value) => option.project_id === value.project_id}
            loading={availableProjectsLoading}
            disabled={availableProjectsLoading}
            noOptionsText="No matching projects found"
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatClockOutProjectLabel(option)}
                  </Typography>
                  {getClockOutProjectSecondaryLabel(option) ? (
                    <Typography variant="caption" color="text.secondary">
                      {getClockOutProjectSecondaryLabel(option)}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="Project Allocation"
                helperText={availableProjectsLoading
                  ? 'Loading your assigned projects...'
                  : 'Search or select an assigned project, or choose Not allocated.'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {availableProjectsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="Reason for Change (Required)"
            multiline
            rows={3}
            fullWidth
            margin="normal"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why are you making this change?"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MyTime() {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [clockStatusLoading, setClockStatusLoading] = useState(true);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [activeEntry, setActiveEntry] = useState(null);
  const [clockActionLoading, setClockActionLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [weeklyTotal, setWeeklyTotal] = useState(0);
  const [weeklyLeave, setWeeklyLeave] = useState([]);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [requireProjectOnClockOut, setRequireProjectOnClockOut] = useState(false);
  const [restrictClockLocation, setRestrictClockLocation] = useState(false);
  const [clockOutProjects, setClockOutProjects] = useState([]);
  const [clockOutProjectsLoading, setClockOutProjectsLoading] = useState(false);
  const [clockOutDialogOpen, setClockOutDialogOpen] = useState(false);
  const [selectedClockOutProject, setSelectedClockOutProject] = useState('');
  const [challengedEntries, setChallengedEntries] = useState([]);
  const challengedSectionRef = useRef(null);

  // Timer state
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);

  // Dialog states
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const currentWeek = getWeekBoundaries(new Date(Date.now() + weekOffset * 7 * 24 * 60 * 60 * 1000));
  const currentWeekStartIso = currentWeek.start.toISOString();
  const currentWeekEndIso = currentWeek.end.toISOString();

  // Fetch clock status
  const fetchClockStatus = useCallback(async () => {
    try {
      const response = await timeEntriesApi.getStatus();
      setIsClockedIn(response.data.is_clocked_in);
      setActiveEntry(response.data.entry);
    } catch (error) {
      console.error('Failed to fetch clock status:', error);
    } finally {
      setClockStatusLoading(false);
    }
  }, []);

  const loadClockOutProjects = useCallback(async () => {
    setClockOutProjectsLoading(true);
    try {
      const response = await projectsApi.getAll('Sales Order');
      const projects = response.data || [];
      setClockOutProjects(projects);
      return projects;
    } finally {
      setClockOutProjectsLoading(false);
    }
  }, []);

  const fetchClockOutSettings = useCallback(async () => {
    try {
      const response = await tenantApi.getBranding();
      const requiresProject = Boolean(response.data?.branding?.require_project_when_clocking_out);
      const requiresClockLocation = Boolean(response.data?.branding?.restrict_clock_location);
      setRequireProjectOnClockOut(requiresProject);
      setRestrictClockLocation(requiresClockLocation);
      if (requiresProject) {
        await loadClockOutProjects();
      } else {
        setClockOutProjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch clock-out settings:', error);
    }
  }, [loadClockOutProjects]);

  const fetchChallengedEntries = useCallback(async () => {
    try {
      const response = await timeEntriesApi.getQueried();
      setChallengedEntries(response.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch challenged entries:', error);
    }
  }, []);

  // Fetch entries for the current week
  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await timeEntriesApi.getMyEntries(
        currentWeekStartIso,
        currentWeekEndIso
      );
      setEntries(response.data.entries);

      const total = response.data.entries.reduce((sum, entry) => {
        return sum + (entry.duration || 0);
      }, 0);
      setWeeklyTotal(total);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setIsLoading(false);
    }
    try {
      const leaveResponse = await leaveRequestsApi.getMyRequests(
        currentWeekStartIso.slice(0, 10),
        currentWeekEndIso.slice(0, 10)
      );
      setWeeklyLeave((leaveResponse.data.requests || []).filter((item) => !['cancelled', 'rejected'].includes(item.status)));
    } catch (error) {
      // Leave context is supplementary and must never stop the timesheet loading.
      console.error('Failed to fetch weekly leave context:', error);
      setWeeklyLeave([]);
    }
  }, [currentWeekEndIso, currentWeekStartIso]);

  useEffect(() => {
    fetchClockStatus();
    fetchEntries();
    fetchClockOutSettings();
    fetchChallengedEntries();
  }, [fetchClockStatus, fetchEntries, fetchClockOutSettings, fetchChallengedEntries]);

  useEffect(() => {
    if (searchParams.get('focus') === 'challenged' && challengedSectionRef.current) {
      challengedSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [challengedEntries, searchParams]);

  // Update timer
  useEffect(() => {
    if (isClockedIn && activeEntry) {
      const clockInTime = parseTimeEntryDate(activeEntry.clock_in_time).getTime();
      
      const now = Date.now();
      setElapsedTime(Math.floor((now - clockInTime) / 1000));
      
      timerRef.current = setInterval(() => {
        const currentTime = Date.now();
        setElapsedTime(Math.floor((currentTime - clockInTime) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setElapsedTime(0);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isClockedIn, activeEntry]);

  const handleClockIn = async () => {
    setClockActionLoading(true);
    try {
      if (!restrictClockLocation) {
        const response = await timeEntriesApi.clockIn({});
        setIsClockedIn(true);
        setActiveEntry(response.data.entry);
        setAlert({ show: true, message: 'You have clocked in successfully!', severity: 'success' });
        fetchEntries();
        setClockActionLoading(false);
        return;
      }

      // Get user's location from browser
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const response = await timeEntriesApi.clockIn({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
            setIsClockedIn(true);
            setActiveEntry(response.data.entry);
            setAlert({ show: true, message: 'You have clocked in successfully!', severity: 'success' });
            fetchEntries();
            setClockActionLoading(false);
          } catch (error) {
            setAlert({ 
              show: true, 
              message: error?.response?.data?.error || 'Failed to clock in', 
              severity: 'error' 
            });
            setClockActionLoading(false);
          }
        },
        (error) => {
          setAlert({
            show: true,
            message: 'Unable to access your location. Please enable location permissions in your browser.',
            severity: 'error'
          });
          setClockActionLoading(false);
        }
      );
    } catch (error) {
      setAlert({ 
        show: true, 
        message: error.message || 'Failed to clock in', 
        severity: 'error' 
      });
      setClockActionLoading(false);
    }
  };

  const performClockOut = async (projectSelectionPayload = {}) => {
    setClockActionLoading(true);
    try {
      if (!restrictClockLocation) {
        const response = await timeEntriesApi.clockOut({
          ...projectSelectionPayload,
        });
        setIsClockedIn(false);
        setActiveEntry(null);
        setClockOutDialogOpen(false);
        setSelectedClockOutProject('');
        const hours = response.data.entry.duration || 0;
        setAlert({
          show: true,
          message: `You have clocked out. Session duration: ${hours.toFixed(2)} hours`,
          severity: 'success'
        });
        fetchEntries();
        setClockActionLoading(false);
        return;
      }

      // Get user's location from browser
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const response = await timeEntriesApi.clockOut({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              ...projectSelectionPayload,
            });
            setIsClockedIn(false);
            setActiveEntry(null);
            setClockOutDialogOpen(false);
            setSelectedClockOutProject('');
            const hours = response.data.entry.duration || 0;
            setAlert({ 
              show: true, 
              message: `You have clocked out. Session duration: ${hours.toFixed(2)} hours`, 
              severity: 'success' 
            });
            fetchEntries();
            setClockActionLoading(false);
          } catch (error) {
            setAlert({ 
              show: true, 
              message: error?.response?.data?.error || 'Failed to clock out', 
              severity: 'error' 
            });
            setClockActionLoading(false);
          }
        },
        (error) => {
          setAlert({
            show: true,
            message: 'Unable to access your location. Please enable location permissions in your browser.',
            severity: 'error'
          });
          setClockActionLoading(false);
        }
      );
    } catch (error) {
      setAlert({ 
        show: true, 
        message: error.message || 'Failed to clock out', 
        severity: 'error' 
      });
      setClockActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!requireProjectOnClockOut) {
      await performClockOut();
      return;
    }

    try {
      const projects = clockOutProjects.length > 0 ? clockOutProjects : await loadClockOutProjects();
      setSelectedClockOutProject(
        projects.length === 1
          ? String(projects[0].project_id)
          : (projects.length === 0 ? String(NOT_ALLOCATED_PROJECT_SENTINEL_ID) : '')
      );
      setClockOutDialogOpen(true);
    } catch (error) {
      setAlert({
        show: true,
        message: 'Failed to load your assigned projects for clock out.',
        severity: 'error',
      });
    }
  };

  const handleManualEntry = async (data) => {
    await timeEntriesApi.createManual(data);
    setAlert({ show: true, message: 'Manual entry submitted for approval.', severity: 'success' });
    fetchClockStatus();
    fetchEntries();
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    loadClockOutProjects().catch(() => {
      setAlert({
        show: true,
        message: 'Failed to load your assigned projects for editing.',
        severity: 'error',
      });
    });
    setEditDialogOpen(true);
  };

  const handleSaveEntry = async (entryId, data) => {
    const challengedMatch = challengedEntries.find((entry) => entry.time_entry_id === entryId);
    const entryMatch = challengedMatch || entries.find((entry) => entry.time_entry_id === entryId);
    const wasQueried = Boolean(entryMatch?.is_queried);
    const response = await timeEntriesApi.update(entryId, data);
    const updatedEntry = response.data?.entry;

    if (updatedEntry) {
      setEntries((currentEntries) => currentEntries.map((entry) => (
        entry.time_entry_id === entryId ? updatedEntry : entry
      )));
      setChallengedEntries((currentEntries) => currentEntries.filter((entry) => entry.time_entry_id !== entryId));
    }

    setAlert({
      show: true,
      message: wasQueried
        ? 'Response sent. Entry resubmitted for approval.'
        : 'Time entry updated. Changes require approval.',
      severity: 'success'
    });
    fetchEntries();
    fetchChallengedEntries();
  };

  const formatWeekRange = () => {
    return `${currentWeek.start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - ${currentWeek.end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  };

  // Group entries by date
  const entriesByDate = entries.reduce((acc, entry) => {
    const dateKey = getTimeEntryDayKey(entry.clock_in_time);
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(entry);
    return acc;
  }, {});

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {alert.show && (
        <Alert 
          severity={alert.severity} 
          onClose={() => setAlert({ ...alert, show: false })}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccessTimeIcon fontSize="large" />
        My Time
      </Typography>

      {/* Clock In/Out Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Time Clock</Typography>
        
        {clockStatusLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: isClockedIn ? 'success.main' : 'error.main',
                }}
              />
              <Typography variant="h6">
                {isClockedIn ? 'Clocked In' : 'Clocked Out'}
              </Typography>
            </Box>

            {isClockedIn && activeEntry && (
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Session started at {formatTimeEntryTime(activeEntry.clock_in_time)}
                </Typography>
                <Typography variant="h2" color="primary" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {formatElapsedTime(elapsedTime)}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                color={isClockedIn ? 'error' : 'success'}
                startIcon={isClockedIn ? <StopIcon /> : <PlayArrowIcon />}
                onClick={isClockedIn ? handleClockOut : handleClockIn}
                disabled={clockActionLoading}
                sx={{ minWidth: 150 }}
              >
                {clockActionLoading ? <CircularProgress size={24} /> : (isClockedIn ? 'Clock Out' : 'Clock In')}
              </Button>

              <Button
                variant="outlined"
                onClick={() => setManualDialogOpen(true)}
              >
                {isClockedIn ? 'Manual clock out' : 'Manual clock in'}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }} id="challenged-entries" ref={challengedSectionRef}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Challenged Entries</Typography>
          <Chip label={challengedEntries.length} color={challengedEntries.length > 0 ? 'error' : 'default'} />
        </Box>

        {challengedEntries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No challenged entries right now.
          </Typography>
        ) : (
          challengedEntries.map((entry) => (
            <Box
              key={entry.time_entry_id}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                bgcolor: 'background.default',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Typography fontWeight={600}>
                  {formatTimeEntryDate(entry.clock_in_time, {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </Typography>
                <Chip label="Challenged" color="error" size="small" />
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {formatTimeEntryTime(entry.clock_in_time)}
                {entry.clock_out_time && ` - ${formatTimeEntryTime(entry.clock_out_time)}`}
                {' · '}
                {formatDuration(entry.duration)}
              </Typography>
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                <strong>Query reason:</strong> {entry.query_reason || 'No query reason provided.'}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Notes: {entry.notes || '-'}
              </Typography>
              <Button variant="contained" color="warning" onClick={() => handleEditEntry(entry)}>
                Respond
              </Button>
            </Box>
          ))
        )}
      </Paper>

      {/* Weekly Timesheet */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => setWeekOffset(weekOffset - 1)}>
            <NavigateBeforeIcon />
          </IconButton>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6">{formatWeekRange()}</Typography>
            <Typography variant="body2" color="primary">
              Total: {formatDuration(weeklyTotal)}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setWeekOffset(weekOffset + 1)}
            disabled={weekOffset >= 0}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {weeklyLeave.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Time off this week</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {weeklyLeave.map((item) => (
                <Chip
                  key={item.leave_request_id}
                  label={`${item.leave_type[0].toUpperCase() + item.leave_type.slice(1)}: ${item.start_date}${item.end_date !== item.start_date ? ` – ${item.end_date}` : ''} (${item.status})`}
                  color={item.status === 'approved' ? 'success' : (item.status === 'queried' ? 'error' : 'warning')}
                  variant="outlined"
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">Time off is shown for context and is not included in worked-hour totals.</Typography>
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : entries.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No time entries for this week
          </Typography>
        ) : (
          Object.entries(entriesByDate)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([dateKey, dateEntries]) => {
              const dailyTotal = dateEntries.reduce((sum, e) => sum + (e.duration || 0), 0);

              return (
                <Box key={dateKey} sx={{ mb: 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    p: 1,
                    borderRadius: 1,
                    mb: 1,
                  }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {formatTimeEntryDate(dateEntries[0].clock_in_time, {
                        weekday: 'long',
                        day: '2-digit',
                        month: 'short',
                      })}
                    </Typography>
                    <Typography variant="subtitle1" color="primary">
                      {formatDuration(dailyTotal)}
                    </Typography>
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Time</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dateEntries.map((entry) => (
                          <TableRow key={entry.time_entry_id}>
                            <TableCell>
                              {formatTimeEntryTime(entry.clock_in_time)}
                              {entry.clock_out_time && (
                                <> - {formatTimeEntryTime(entry.clock_out_time)}</>
                              )}
                              {!entry.clock_out_time && (
                                <Chip label="Active" color="success" size="small" sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell>{formatDuration(entry.duration)}</TableCell>
                            <TableCell>
                              <Chip label={getEntryStatus(entry).label} color={getEntryStatus(entry).color} size="small" />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {entry.notes || '-'}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => handleEditEntry(entry)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })
        )}
      </Paper>

      <WorkerLeaveSection />

      <ManualEntryDialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
        onSubmit={handleManualEntry}
        isClockedIn={isClockedIn}
        activeClockInTime={activeEntry?.clock_in_time}
        requireProjectOnClockOut={requireProjectOnClockOut}
        availableProjects={clockOutProjects}
        availableProjectsLoading={clockOutProjectsLoading}
      />

      <ClockOutProjectDialog
        open={clockOutDialogOpen}
        onClose={() => {
          if (!clockActionLoading) {
            setClockOutDialogOpen(false);
            setSelectedClockOutProject('');
          }
        }}
        onConfirm={() => performClockOut(getClockOutProjectPayloadValue(selectedClockOutProject))}
        projects={clockOutProjects}
        selectedProjectId={selectedClockOutProject}
        onProjectChange={setSelectedClockOutProject}
        loading={clockActionLoading}
      />

      <EditEntryDialog
        open={editDialogOpen}
        entry={selectedEntry}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedEntry(null);
        }}
        onSave={handleSaveEntry}
        availableProjects={clockOutProjects}
        availableProjectsLoading={clockOutProjectsLoading}
      />
    </Container>
  );
}
